import { getManagePlanFeatureListBulletsUpToTier } from '@/helpers/manage-plan-feature-display';

const PRODUCT_CLAIM_BASE = 'subscription.organization.planPage.productClaim';
const PRODUCT_CLAIM_TIER_CANDIDATES = ['FREE', 'BASIC', 'SMART', 'PRO', 'PREMIUM'] as const;

/**
 * Returns a translated string when the composed key exists; otherwise `undefined`.
 * Matches next-intl / i18next behavior where missing keys resolve to the key string.
 *
 * When `tier` is set, prefers `baseKey.productKey.tier` and falls back to `baseKey.productKey`
 * so product-level claims still work when no tier-specific copy exists.
 */
export function resolveTranslatedProductField(
  t: (key: string, params?: Record<string, string | number>) => string,
  baseKey: string,
  productKey: string,
  tier?: string,
): string | undefined {
  const pk = productKey.trim();
  if (!pk) {
    return undefined;
  }
  const tierTrimmed = tier?.trim();
  if (tierTrimmed) {
    const tierKey = `${baseKey}.${pk}.${tierTrimmed}`;
    const tierResolved = t(tierKey);
    if (tierResolved !== tierKey && typeof tierResolved === 'string') {
      return tierResolved;
    }
  }
  const i18nKey = `${baseKey}.${pk}`;
  const resolved = t(i18nKey);
  // Nested objects (tier maps) must not be treated as a product-level string claim.
  if (resolved === i18nKey || typeof resolved !== 'string') {
    return undefined;
  }
  return resolved;
}

function productClaimHasAnyTier(
  t: (key: string, params?: Record<string, string | number>) => string,
  productKey: string,
): boolean {
  return PRODUCT_CLAIM_TIER_CANDIDATES.some((candidate) => {
    const key = `${PRODUCT_CLAIM_BASE}.${productKey}.${candidate}`;
    return t(key) !== key;
  });
}

function resolveProductClaimPlanWide(
  t: (key: string, params?: Record<string, string | number>) => string,
  productKey: string,
): string | undefined {
  const planKey = `${PRODUCT_CLAIM_BASE}.${productKey}.plan`;
  const planResolved = t(planKey);
  if (planResolved !== planKey && typeof planResolved === 'string') {
    return planResolved;
  }
  return undefined;
}

/**
 * Product marketing claim for a plan line:
 * 1. `productClaim.<productKey>.<tier>` when present
 * 2. else `productClaim.<productKey>.plan` when `allowPlanWideClaim` is true (default)
 * 3. else `productClaim.<productKey>` when that is a string leaf (no tier/`plan` map under the product)
 * 4. else comma-separated `managePlanFeatureLists` bullets from the lowest tier through `tier`
 *
 * Cart/checkout modals should pass `{ allowPlanWideClaim: false }` so missing tier claims
 * fall through to feature lists instead of the plan-wide claim.
 *
 * If other tiers have claims but this tier does not, still try `.plan` (when allowed) before (4).
 * Skip (3) when a tier map exists so a sibling tier key does not block the feature-list fallback.
 */
export function resolveProductClaimText(
  t: (key: string, params?: Record<string, string | number>) => string,
  productKey: string,
  tier?: string,
  options?: { allowPlanWideClaim?: boolean },
): string | undefined {
  const pk = productKey.trim();
  if (!pk) {
    return undefined;
  }
  const allowPlanWideClaim = options?.allowPlanWideClaim !== false;
  const tierTrimmed = tier?.trim();

  if (tierTrimmed) {
    const tierKey = `${PRODUCT_CLAIM_BASE}.${pk}.${tierTrimmed}`;
    const tierResolved = t(tierKey);
    if (tierResolved !== tierKey && typeof tierResolved === 'string') {
      return tierResolved;
    }

    if (allowPlanWideClaim) {
      const planWide = resolveProductClaimPlanWide(t, pk);
      if (planWide) {
        return planWide;
      }
    }

    // Tier map exists for this product but not this tier → feature lists, not product-level.
    if (!productClaimHasAnyTier(t, pk)) {
      const productLevel = resolveTranslatedProductField(t, PRODUCT_CLAIM_BASE, pk);
      if (productLevel) {
        return productLevel;
      }
    }

    const bullets = getManagePlanFeatureListBulletsUpToTier(t, pk, tierTrimmed);
    return bullets.length ? bullets.join(', ') : undefined;
  }

  if (allowPlanWideClaim) {
    const planWide = resolveProductClaimPlanWide(t, pk);
    if (planWide) {
      return planWide;
    }
  }
  return resolveTranslatedProductField(t, PRODUCT_CLAIM_BASE, pk);
}
